import { useCallback, useState } from "react";
import AddIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { Checkbox, FormControl, FormControlLabel, FormHelperText, styled } from "@mui/material";
import Button from "@components/Button/Button";
import FormDivider from "@components/FormDivider/FormDivider";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import FormSection from "@components/FormSection/FormSection";
import Input from "@components/Input/Input";
import type { OffshoreGatepassFormData } from "@features/gatepass/schema/offshoreGatepassFormSchema";
import { lookupVesselByImo } from "@services/gatepassApi";
import { useMutation } from "@tanstack/react-query";
import { runFieldNavigation } from "@telemetry/runFieldNavigation";
import { runInquiry } from "@telemetry/runInquiry";
import { t } from "i18next";
import { Controller, FormProvider, useFieldArray, type UseFormReturn } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { useOffshoreGatepassFormStore } from "../../store/useOffshoreGatepassForm.store";

export interface FormContext {
  formData: UseFormReturn<OffshoreGatepassFormData>;
  updateFormData: (key: string, value: unknown) => void;
}

const TRANSLATION_KEY = "gatepass";
const VESSEL_LOOKUP_INQUIRY_CONTEXT = {
  module: "gatepass",
  screen: "Offshore Gatepass Vessel Details",
  action: "lookup_vessel_by_imo",
  entityType: "vessel",
};
const VESSEL_LOOKUP_FIELD_NAVIGATION_CONTEXT = {
  module: "gatepass",
  screen: "Offshore Gatepass Vessel Details",
  action: "populate_vessel_name_by_imo",
  fromField: "vesselImoNumber",
  toField: "vesselName",
  entityType: "vessel",
};

const VesselDetails = () => {
  const { formData, updateFormData } = useOutletContext<FormContext>();
  const { setField } = useOffshoreGatepassFormStore();
  const [isLookupLoadingByFieldId, setIsLookupLoadingByFieldId] = useState<Record<string, boolean>>(
    {},
  );

  type VesselRow = OffshoreGatepassFormData["vessels"][number];
  const { fields, append, remove } = useFieldArray({
    control: formData.control,
    name: "vessels",
  });
  const vesselLookupMutation = useMutation({
    mutationFn: (imoNumber: number) =>
      runFieldNavigation(VESSEL_LOOKUP_FIELD_NAVIGATION_CONTEXT, () =>
        runInquiry(VESSEL_LOOKUP_INQUIRY_CONTEXT, () =>
          lookupVesselByImo({
            service: "bff-gatepass",
            payload: { imoNumber },
          }),
        ),
      ),
  });

  const syncVessels = useCallback(() => {
    const next = (formData.getValues("vessels") as VesselRow[]) ?? [];
    setField("vessels", next as OffshoreGatepassFormData["vessels"]);
    updateFormData("vessels", next);
  }, [formData, setField, updateFormData]);

  const syncVesselRow = useCallback(
    (rowIndex: number) => {
      const next = (formData.getValues("vessels") as VesselRow[]) ?? [];
      if (!next[rowIndex]) return;
      setField("vessels", next as OffshoreGatepassFormData["vessels"]);
      updateFormData("vessels", next);
    },
    [formData, setField, updateFormData],
  );
  const addVesselButton = (
    <Button
      startIcon={<AddIcon />}
      type="button"
      variant="outlined"
      label={t(`${TRANSLATION_KEY}.add-vessel`, { defaultValue: "Add New Vessel" })}
      testId="offshore-vessels-add-button"
      onClick={() => {
        append({ vesselName: "", vesselImoNumber: "", noImoNumber: false });
        setTimeout(() => syncVessels(), 0);
      }}
    />
  );

  const checkImoAndPopulateName = useCallback(
    async (idx: number, fieldId: string) => {
      const imoPath = `vessels.${idx}.vesselImoNumber` as const;
      const vesselNamePath = `vessels.${idx}.vesselName` as const;
      const noImoPath = `vessels.${idx}.noImoNumber` as const;

      const noImo = Boolean(formData.getValues(noImoPath));
      if (noImo) return;

      const rawImo = String(formData.getValues(imoPath) ?? "").trim();
      if (!rawImo) {
        formData.setError(imoPath, {
          type: "manual",
          message: t("form-validation.required", {
            label: t(`${TRANSLATION_KEY}.vessel-imo-number`, { defaultValue: "Vessel IMO Number" }),
          }),
        });
        return;
      }
      if (!/^\d+$/.test(rawImo)) {
        formData.setError(imoPath, {
          type: "manual",
          message: t("form-validation.invalid-number", {
            label: t(`${TRANSLATION_KEY}.vessel-imo-number`, { defaultValue: "Vessel IMO Number" }),
          }),
        });
        return;
      }
      if (rawImo.length > 8) {
        formData.setError(imoPath, {
          type: "manual",
          message: t("form-validation.max-length", {
            label: t(`${TRANSLATION_KEY}.vessel-imo-number`, { defaultValue: "Vessel IMO Number" }),
            max: 8,
          }),
        });
        return;
      }

      setIsLookupLoadingByFieldId((prev) => ({ ...prev, [fieldId]: true }));
      try {
        const response = await vesselLookupMutation.mutateAsync(Number(rawImo));
        const vesselName = String(response?.name ?? "").trim();
        if (!vesselName) {
          formData.setError(imoPath, {
            type: "manual",
            message: t("gatepass.vessel-not-registered", {
              defaultValue: "Vessel not registered!",
            }),
          });
          formData.setValue(vesselNamePath, "", { shouldDirty: true, shouldValidate: true });
          return;
        }
        // Do not clearErrors here: it wipes resolver errors (e.g. duplicate IMO) after
        formData.setValue(vesselNamePath, vesselName, {
          shouldDirty: true,
          shouldValidate: false,
        });
        await formData.trigger("vessels");
        syncVesselRow(idx);
      } catch (error: any) {
        const fallbackMsg = t("gatepass.vessel-not-registered", {
          defaultValue: "Vessel not registered!",
        });
        const apiMessage =
          error?.response?.data?.detail || error?.response?.data?.title || error?.message;
        formData.setError(imoPath, {
          type: "manual",
          message: String(apiMessage || fallbackMsg),
        });
      } finally {
        setIsLookupLoadingByFieldId((prev) => ({ ...prev, [fieldId]: false }));
      }
    },
    [formData, syncVesselRow, vesselLookupMutation],
  );

  return (
    <FormProvider {...formData}>
      <FormSection
        title={t(`${TRANSLATION_KEY}.vessel-details`, { defaultValue: "Vessel details" })}
        button={addVesselButton}
      >
        <div className="col-span-2">
          <FormControl
            fullWidth
            sx={(theme) => ({
              "mb": 4,
              "& .MuiFormLabel-root": { color: theme.palette.text.primary },
              "& .MuiFormLabel-root.Mui-focused": { color: theme.palette.text.primary },
              "& .MuiFormLabel-root.Mui-error": { color: theme.palette.text.primary },
            })}
          >
            {typeof formData.formState.errors?.vessels?.message === "string" && (
              <FormHelperText error sx={{ mb: 1 }}>
                {String(formData.formState.errors.vessels.message)}
              </FormHelperText>
            )}
            <div className="space-y-4">
              {fields.map((field, idx) => {
                const vesselNamePath = `vessels.${idx}.vesselName` as const;
                const imoPath = `vessels.${idx}.vesselImoNumber` as const;
                const noImoPath = `vessels.${idx}.noImoNumber` as const;
                const noImo = Boolean(formData.watch(noImoPath));

                const vesselNameError =
                  formData.formState.errors?.vessels?.[idx]?.vesselName?.message;
                const imoError =
                  formData.formState.errors?.vessels?.[idx]?.vesselImoNumber?.message;

                return (
                  <div key={field.id} className="space-y-4">
                    {idx > 0 && <FormDivider />}

                    {fields.length > 1 && (
                      <div className="col-span-2 flex justify-end pt-4">
                        <StyledRemoveButton
                          startIcon={<CloseIcon />}
                          type="button"
                          disableElevation
                          variant="contained"
                          label={<span className="normal-case font-medium">Remove</span>}
                          testId={`offshore-vessel-remove-${idx}`}
                          onClick={() => {
                            remove(idx);
                            syncVessels();
                            // Field array removal does not re-run the resolver; stale duplicate errors
                            // would stay on the remaining row(s) until we validate again.
                            queueMicrotask(() => {
                              void formData.trigger("vessels");
                            });
                          }}
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                      <FormControl fullWidth disabled={!noImo}>
                        <CustomFormLabel
                          data-testid={`offshore-vessel-name-${idx}`}
                          disabled={!noImo}
                          sx={{ mb: 1, display: "block" }}
                        >
                          {t(`${TRANSLATION_KEY}.vessel-name`, { defaultValue: "Vessel Name" })}
                        </CustomFormLabel>
                        <Controller
                          name={vesselNamePath}
                          control={formData.control}
                          render={({ field: nameField }) => (
                            <Input
                              {...nameField}
                              fullWidth
                              placeholder={t(`${TRANSLATION_KEY}.vessel-name`, {
                                defaultValue: "Vessel Name",
                              })}
                              testId={`offshore-vessel-name-input-${idx}`}
                              error={Boolean(vesselNameError)}
                              disabled={!noImo}
                              onChange={(e) => {
                                nameField.onChange(e.target.value);
                                syncVesselRow(idx);
                              }}
                            />
                          )}
                        />
                        {vesselNameError && (
                          <FormHelperText error>{String(vesselNameError)}</FormHelperText>
                        )}
                      </FormControl>
                      <FormControl fullWidth disabled={noImo}>
                        <CustomFormLabel
                          data-testid={`offshore-vessel-imo-${idx}`}
                          disabled={noImo}
                          sx={{ mb: 1, display: "block" }}
                        >
                          {t(`${TRANSLATION_KEY}.vessel-imo-number`, {
                            defaultValue: "Vessel IMO Number",
                          })}
                        </CustomFormLabel>
                        <Controller
                          name={imoPath}
                          control={formData.control}
                          render={({ field: imoField }) => (
                            <Input
                              {...imoField}
                              fullWidth
                              placeholder={t(`${TRANSLATION_KEY}.vessel-imo-number`, {
                                defaultValue: "Vessel IMO Number",
                              })}
                              error={Boolean(imoError)}
                              testId={`offshore-vessel-imo-input-${idx}`}
                              disabled={noImo}
                              onChange={(e) => {
                                imoField.onChange(e.target.value);
                                // IMO changed: clear previously auto populated vessel name
                                formData.setValue(vesselNamePath, "", {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                });
                                syncVesselRow(idx);
                              }}
                              onBlur={() => {
                                imoField.onBlur();
                                if (!noImo) {
                                  void checkImoAndPopulateName(idx, field.id);
                                }
                              }}
                            />
                          )}
                        />
                        {imoError && <FormHelperText error>{String(imoError)}</FormHelperText>}
                        {isLookupLoadingByFieldId[field.id] && (
                          <FormHelperText>
                            {t("common.loading", { defaultValue: "Loading..." })}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                      <Controller
                        name={noImoPath}
                        control={formData.control}
                        render={({ field: noImoField }) => (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={Boolean(noImoField.value)}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  noImoField.onChange(checked);
                                  if (checked) {
                                    formData.clearErrors(imoPath);
                                    formData.setValue(imoPath, "", {
                                      shouldDirty: true,
                                      shouldValidate: true,
                                    });
                                  } else {
                                    formData.setValue(vesselNamePath, "", {
                                      shouldDirty: true,
                                      shouldValidate: true,
                                    });
                                  }
                                  syncVesselRow(idx);
                                }}
                              />
                            }
                            label={t(`${TRANSLATION_KEY}.no-imo-number`, {
                              defaultValue: "No IMO number",
                            })}
                          />
                        )}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </FormControl>
        </div>
      </FormSection>
    </FormProvider>
  );
};

export default VesselDetails;

const StyledRemoveButton = styled(Button)(() => ({
  "color": "#333333",
  "backgroundColor": "#F1F2F4",
  "&:hover": {
    backgroundColor: "#F1F2F4",
  },
}));
